import { XIcon } from "lucide-react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RoomSystemOption } from "@/features/session/types/room-system-option";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

type RegisterRoomSystemToEventModalTableProps = {
  roomSystems: RoomSystemOption[];
  setRoomSystems: (
    value:
      | RoomSystemOption[]
      | ((prev: RoomSystemOption[]) => RoomSystemOption[]),
  ) => void;
};

export function RegisterRoomSystemToEventModalTable({
  roomSystems,
  setRoomSystems,
}: RegisterRoomSystemToEventModalTableProps) {
  const columnHelper = createColumnHelper<RoomSystemOption>();

  const removeRoomSystemFromEvent = (roomSystemId: string) => {
    setRoomSystems((prevRoomSystems: RoomSystemOption[]) =>
      prevRoomSystems.filter(
        (roomSystem: RoomSystemOption) => roomSystem.id !== roomSystemId,
      ),
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        header: "Room System",
        cell: ({ row }) => {
          return <div>{row.original.name}</div>;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => removeRoomSystemFromEvent(row.original.id)}
              >
                <XIcon className="size-4" aria-hidden={true} />
              </Button>
            </div>
          );
        },
      }),
    ],
    [columnHelper, removeRoomSystemFromEvent],
  );

  const table = useReactTable({
    data: roomSystems ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (roomSystems.length === 0) {
    return null;
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
